/* eslint-disable react-hooks/exhaustive-deps */
/* รายละเอียดเรื่องที่ลูกค้าแจ้ง (RC)   /portal/rc/:code */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Flex, Input, Popconfirm, Space, Spin, Typography, message } from "antd";
import { LeftOutlined, SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import FileList from "../components/ak/FileList";
import FilePicker from "../components/ak/FilePicker";
import RcTimeline from "../components/ak/RcTimeline";
import { RC_CLOSED, RcStatusTag, RcTypeTag, costText } from "../components/ak/rc";
import { openFile } from "../utils/fileOpen";
import { Portal, perr } from "./portalApi";

/* ขั้นตอนที่ลูกค้าเห็น */
const stepOf = (s) => ({ รอรับเรื่อง: 0, รับเรื่องแล้ว: 1, อนุมัติ: 2, ไม่อนุมัติ: 2, เสร็จสิ้น: 3 }[s] ?? 0);

export default function PortalRc() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [rc, setRc] = useState(null);
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () =>
    Portal.rc(code)
      .then((r) => setRc(r.data.data))
      .catch((e) => {
        message.error(perr(e));
        navigate("/portal", { replace: true });
      });

  useEffect(() => {
    load();
  }, [code]);

  if (!rc) return <Flex justify="center" style={{ padding: 60 }}><Spin /></Flex>;

  const open = (f, inline) => openFile(() => Portal.file("rc", f.file_id, inline), f, inline).catch((e) => message.error(e.message));
  const closed = RC_CLOSED.includes(rc.status);

  const send = async () => {
    setBusy(true);
    try {
      const r = await Portal.replyRc({ rc_code: code, note: note.trim() || undefined }, files);
      setRc(r.data.data);
      setNote("");
      setFiles([]);
      message.success("ส่งแล้ว");
    } catch (e) {
      message.error(perr(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () =>
    Portal.cancelRc(code)
      .then((r) => {
        setRc(r.data.data);
        message.success("ยกเลิกเรื่องแล้ว");
      })
      .catch((e) => message.error(perr(e)));

  return (
    <div>
      <Button type="link" icon={<LeftOutlined />} onClick={() => navigate(`/portal/project/${rc.project_code}?tab=rc`)} className="pt-back">
        {rc.project_name}
      </Button>

      <Card className="pt-card">
        <Flex justify="space-between" align="flex-start" gap={8}>
          <div style={{ minWidth: 0 }}>
            <Typography.Title level={4} className="!m-0">
              {rc.title}
            </Typography.Title>
            <div className="pt-meta">
              <RcTypeTag value={rc.rc_type} /> {rc.rc_code} · แจ้งเมื่อ {dayjs(rc.created_date).format("DD/MM/YYYY HH:mm")}
            </div>
          </div>
          <RcStatusTag value={rc.status} />
        </Flex>

        {rc.status === "ยกเลิก" ? (
          <Alert className="mt-3" type="warning" message="เรื่องนี้ถูกยกเลิกแล้ว" />
        ) : (
          <div className="pt-steps">
            {["แจ้ง", "รับเรื่อง", rc.status === "ไม่อนุมัติ" ? "ไม่อนุมัติ" : "อนุมัติ", "เสร็จ"].map((t, i) => {
              const cur = stepOf(rc.status);
              const done = i < cur || rc.status === "เสร็จสิ้น";
              const err = rc.status === "ไม่อนุมัติ" && i === 2;
              return (
                <div key={i} className={`pt-step ${done ? "done" : ""} ${i === cur && !done ? "cur" : ""} ${err ? "err" : ""}`}>
                  <div className="dot">{done && !err ? "✓" : i + 1}</div>
                  <div className="t">{t}</div>
                </div>
              );
            })}
          </div>
        )}

        {costText(rc.cost_change) && (
          <div className="pt-cost">
            ค่าใช้จ่ายที่เปลี่ยน <b>{costText(rc.cost_change)}</b>
            <span className="pt-meta"> {Number(rc.cost_change) > 0 ? "(งานเพิ่ม)" : Number(rc.cost_change) < 0 ? "(งานลด)" : ""}</span>
          </div>
        )}

        {rc.staff_reply && (
          <Alert
            className="mt-3"
            type="info"
            showIcon
            message={`ตอบกลับจากเจ้าหน้าที่${rc.replied_date ? ` · ${dayjs(rc.replied_date).format("DD/MM/YYYY HH:mm")}` : ""}`}
            description={<div style={{ whiteSpace: "pre-wrap" }}>{rc.staff_reply}</div>}
          />
        )}

        {rc.detail && (
          <div className="mt-3">
            <div className="pt-label">รายละเอียดที่แจ้ง</div>
            <div style={{ whiteSpace: "pre-wrap" }}>{rc.detail}</div>
          </div>
        )}
      </Card>

      <Card className="pt-card" title={`ไฟล์แนบ (${rc.files.length})`}>
        <FileList files={rc.files} onOpen={open} emptyText="ไม่มีไฟล์แนบ" />
      </Card>

      <Card className="pt-card" title="ความคืบหน้า / พูดคุยกับเจ้าหน้าที่">
        <RcTimeline logs={rc.logs} viewer="customer" />
        {!closed && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Input.TextArea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} placeholder="พิมพ์ข้อความเพิ่มเติมถึงเจ้าหน้าที่" />
            <FilePicker value={files} onChange={setFiles} mode="button" />
            <Flex justify="space-between" gap={8} wrap="wrap">
              {rc.status === "รอรับเรื่อง" ? (
                <Popconfirm title="ยกเลิกเรื่องนี้?" onConfirm={cancel} okText="ยกเลิกเรื่อง" okButtonProps={{ danger: true }} cancelText="ไม่">
                  <Button danger>ยกเลิกเรื่องนี้</Button>
                </Popconfirm>
              ) : (
                <span />
              )}
              <Button type="primary" icon={<SendOutlined />} loading={busy} disabled={!note.trim() && !files.length} onClick={send}>
                ส่ง
              </Button>
            </Flex>
          </Space>
        )}
        {closed && <Typography.Text type="secondary">เรื่องนี้ปิดแล้ว หากต้องการเปลี่ยนแปลงเพิ่ม กรุณาแจ้งเรื่องใหม่</Typography.Text>}
      </Card>
    </div>
  );
}
